import React from 'react';
import { useDispatch } from 'react-redux';
import { NodeToolbar, Position, useReactFlow, type Node, type Edge } from '@xyflow/react';
import { useTranslation } from 'react-i18next';
import { FiTrash2, FiCopy } from 'react-icons/fi';
import {
  MdAlignHorizontalLeft, MdAlignHorizontalCenter, MdAlignHorizontalRight,
  MdAlignVerticalTop, MdAlignVerticalCenter, MdAlignVerticalBottom,
  MdHorizontalDistribute, MdVerticalDistribute,
} from 'react-icons/md';
import type { IconType } from 'react-icons';
import { replaceCanvas, setNodes } from '../../../store/workflow/workflowSlice';
import { showAlert, showConfirm } from '../../common/AppDialog';
import { showToast } from '../../common/Toast';
import { copyPngToClipboard, renderCanvasPng } from '../utils/canvasImage';
import { logError, logUsage } from '../../../services/eventLog';

export type AlignOp = 'left' | 'hcenter' | 'right' | 'top' | 'vcenter' | 'bottom' | 'distributeH' | 'distributeV';

interface Box { id: string; x: number; y: number; w: number; h: number }

/**
 * 정렬·같은 간격 — 절대 좌표 상자들을 받아 새 절대 좌표(id → {x,y})를 돌려준다(순수 함수, 테스트한다).
 * 가운데 맞춤은 선택 전체 경계의 가운데로. 같은 간격은 양 끝 노드를 두고 사이 간격을 고르게(3개 이상일 때).
 */
export function alignBoxes(boxes: Box[], op: AlignOp): Map<string, { x: number; y: number }> {
  const out = new Map<string, { x: number; y: number }>();
  if (boxes.length < 2) return out;
  const minX = Math.min(...boxes.map((b) => b.x));
  const maxR = Math.max(...boxes.map((b) => b.x + b.w));
  const minY = Math.min(...boxes.map((b) => b.y));
  const maxB = Math.max(...boxes.map((b) => b.y + b.h));
  const cx = (minX + maxR) / 2;
  const cy = (minY + maxB) / 2;
  const put = (b: Box, x: number, y: number) => out.set(b.id, { x: Math.round(x), y: Math.round(y) });
  if (op === 'distributeH' || op === 'distributeV') {
    if (boxes.length < 3) return out;
    const horiz = op === 'distributeH';
    const sorted = [...boxes].sort((a, b) => (horiz ? a.x - b.x : a.y - b.y));
    const first = sorted[0];
    const last = sorted[sorted.length - 1];
    const span = horiz ? last.x + last.w - first.x : last.y + last.h - first.y;
    const sizes = sorted.reduce((s, b) => s + (horiz ? b.w : b.h), 0);
    const gap = (span - sizes) / (sorted.length - 1);
    let at = horiz ? first.x : first.y;
    sorted.forEach((b) => {
      if (horiz) put(b, at, b.y); else put(b, b.x, at);
      at += (horiz ? b.w : b.h) + gap;
    });
    return out;
  }
  boxes.forEach((b) => {
    switch (op) {
      case 'left': put(b, minX, b.y); break;
      case 'right': put(b, maxR - b.w, b.y); break;
      case 'hcenter': put(b, cx - b.w / 2, b.y); break;
      case 'top': put(b, b.x, minY); break;
      case 'bottom': put(b, b.x, maxB - b.h); break;
      case 'vcenter': put(b, b.x, cy - b.h / 2); break;
    }
  });
  return out;
}

const ALIGN_BUTTONS: Array<{ op: AlignOp; icon: IconType }> = [
  { op: 'left', icon: MdAlignHorizontalLeft },
  { op: 'hcenter', icon: MdAlignHorizontalCenter },
  { op: 'right', icon: MdAlignHorizontalRight },
  { op: 'top', icon: MdAlignVerticalTop },
  { op: 'vcenter', icon: MdAlignVerticalCenter },
  { op: 'bottom', icon: MdAlignVerticalBottom },
  { op: 'distributeH', icon: MdHorizontalDistribute },
  { op: 'distributeV', icon: MdVerticalDistribute },
];

interface Props {
  nodes: Node[];
  edges: Edge[];
  selectedIds: string[];
  /** 범위 끌기로 고른 선택인가 — 그러면 1개여도 막대를 띄운다(이미지 복사만) */
  boxSelected?: boolean;
  workflowName?: string;
}

/**
 * 범위로 고른 노드(2개 이상)에 대한 도구 — 선택 범위 위에 뜬다. 삭제 · 이미지 복사(선택한 것만, 배경 없이) · 정렬.
 * 바꾸는 동작은 한 액션(replaceCanvas)으로 해서 되돌리기 한 번에 돌아간다.
 */
const SelectionToolbar: React.FC<Props> = ({ nodes, edges, selectedIds, boxSelected, workflowName }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const rf = useReactFlow();
  // 2개 이상이면 전부, 범위로 1개만 골랐으면 이미지 복사만 (정렬은 뜻이 없고, 삭제는 노드의 휴지통 아이콘이 있다)
  const single = selectedIds.length === 1;
  if (selectedIds.length === 0 || (single && !boxSelected)) return null;
  const selected = new Set(selectedIds);

  /** 선택한 그룹 안의 노드까지 — 그룹을 지우면 안의 노드도 함께 */
  const withDescendants = () => {
    const all = new Set(selected);
    let grew = true;
    while (grew) {
      grew = false;
      nodes.forEach((n) => { if (n.parentId && all.has(n.parentId) && !all.has(n.id)) { all.add(n.id); grew = true; } });
    }
    return all;
  };

  const handleDelete = async () => {
    const gone = withDescendants();
    if (!(await showConfirm(t('workflow:selection.deleteConfirm', { n: gone.size }), { title: t('workflow:selection.delete'), okText: t('workflow:selection.deleteOk'), tone: 'danger' }))) return;
    dispatch(replaceCanvas({
      nodes: nodes.filter((n) => !gone.has(n.id)) as any,
      edges: edges.filter((e) => !gone.has(e.source) && !gone.has(e.target)) as any,
    }));
  };

  const handleCopyImage = async () => {
    const keep = withDescendants();
    // 선택 표시(파란 테두리)가 그림에 들어가지 않게 잠깐 선택을 풀고 그린 뒤 되돌린다 — 선택만 바뀌어 되돌리기 기록은 생기지 않는다
    dispatch(setNodes(nodes.map((n) => ({ ...n, selected: false })) as any));
    await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
    try {
      const png = await renderCanvasPng(rf.getNodes(), {
        onlyNodeIds: keep,
        edgeEnds: new Map(edges.map((e) => [e.id, { source: e.source, target: e.target }])),
        transparent: true,      // 화면 테마 그대로(다크 모드면 다크 색) — 배경만 뺀다
        // 다크 모드면 노드 밖 이름·부제만 어두운 글자로 — 밝은 글자는 흰 문서에 붙이면 보이지 않는다
        darkCaptions: document.documentElement.classList.contains('dark'),
      });
      if (!png) return;
      if (await copyPngToClipboard(png.dataUrl)) {
        showToast(t('workflow:selection.copied', { n: keep.size }), 'info');
      } else {
        // 클립보드에 그림을 쓸 수 없는 환경(보안 연결이 아닌 주소 등) — 파일로 내려받는다
        const a = document.createElement('a');
        a.download = `${(workflowName || 'workflow').replace(/[\/:*?"<>|]+/g, '_')}-selection.png`;
        a.href = png.dataUrl;
        a.click();
        showAlert(t('workflow:selection.copyFallback'));
      }
      logUsage('canvas.export', { target: workflowName, detail: { format: 'png-selection', nodes: keep.size } });
    } catch (err) {
      showAlert(t('workflow:toolbar.imageFailed'));
      logError('canvas.export', err, { target: workflowName, detail: { format: 'png-selection' } });
    } finally {
      dispatch(setNodes(nodes.map((n) => ({ ...n, selected: selected.has(n.id) })) as any));
    }
  };

  const handleAlign = (op: AlignOp) => {
    // 그룹과 그 안의 노드를 함께 골랐으면 그룹만 움직인다(안의 노드는 그룹을 따라간다 — 두 번 움직이지 않게)
    const movable = selectedIds.filter((id) => {
      let p = nodes.find((n) => n.id === id)?.parentId;
      while (p) { if (selected.has(p)) return false; p = nodes.find((n) => n.id === p)?.parentId; }
      return true;
    });
    const boxes: Box[] = [];
    const offset = new Map<string, { dx: number; dy: number }>();   // 절대 좌표 - 상대 좌표 (그룹 안 노드)
    movable.forEach((id) => {
      const internal: any = rf.getInternalNode(id);
      const n = nodes.find((x) => x.id === id);
      if (!internal || !n) return;
      const abs = internal.internals?.positionAbsolute ?? n.position;
      boxes.push({ id, x: abs.x, y: abs.y, w: internal.measured?.width ?? n.width ?? 0, h: internal.measured?.height ?? n.height ?? 0 });
      offset.set(id, { dx: abs.x - n.position.x, dy: abs.y - n.position.y });
    });
    const moved = alignBoxes(boxes, op);
    if (!moved.size) return;
    dispatch(replaceCanvas({
      nodes: nodes.map((n) => {
        const to = moved.get(n.id);
        const off = offset.get(n.id);
        return to && off ? { ...n, position: { x: to.x - off.dx, y: to.y - off.dy } } : n;
      }) as any,
      edges: edges as any,
    }));
  };

  const btn = 'p-1.5 rounded hover:bg-gray-100 text-gray-700 disabled:opacity-30 disabled:cursor-not-allowed';
  return (
    <NodeToolbar nodeId={selectedIds} isVisible position={Position.Top} offset={14}>
      <div className="flex items-center gap-0.5 px-1.5 py-1 rounded-md border border-gray-300 bg-white shadow-md text-xs nodrag nopan" data-export-skip>
        {!single && (<>
        <span className="px-1.5 text-gray-500">{t('workflow:selection.count', { n: selectedIds.length })}</span>
        <span className="w-px h-4 bg-gray-200 mx-0.5" />
        {ALIGN_BUTTONS.map(({ op, icon }) => (
          <button key={op} type="button" className={btn} onClick={() => handleAlign(op)}
            disabled={(op === 'distributeH' || op === 'distributeV') && selectedIds.length < 3}
            title={t(`workflow:selection.align.${op}`)}>
            {React.createElement(icon as any, { size: 16 })}
          </button>
        ))}
        <span className="w-px h-4 bg-gray-200 mx-0.5" />
        </>)}
        <button type="button" className={btn} onClick={handleCopyImage} title={t('workflow:selection.copyImage')}>
          {React.createElement(FiCopy as any, { size: 15 })}
        </button>
        {!single && (
          <button type="button" className={`${btn} text-red-600 hover:bg-red-50`} onClick={handleDelete} title={t('workflow:selection.delete')}>
            {React.createElement(FiTrash2 as any, { size: 15 })}
          </button>
        )}
      </div>
    </NodeToolbar>
  );
};

export default SelectionToolbar;
